import React, {useEffect, useRef, useState} from "react";
import * as UserService from "../api/UserService";
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import NotStartedOutlinedIcon from '@mui/icons-material/NotStartedOutlined';
import {Button, ModalBody, ModalFooter, ModalHeader, ModalTitle, OverlayTrigger, Tooltip} from "react-bootstrap";

import VideoRecorderStyle from './VideoRecorder.module.css'
import useWebSocket from "react-use-websocket";
import {sendRecordingStartedUpdate} from "../api/UserService";
import {fetchWithRetry, wsBaseUrl} from "../api/ApiUtils";
import {useTranslation} from "react-i18next";
import VideoRecorderPrestart from "./VideoRecorderPrestart";
import Modal from "react-bootstrap/Modal";


function VideoRecorder(props) {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'videoRecorder' });
    const [recordingInProgress, setRecordingInProgress] = useState(false)
    const [recordingPaused, setRecordingPaused] = useState(false)
    const [recordingRequested, setRecordingRequested] = useState(false)
    const [awsRequest, setAwsRequest] = useState(undefined)
    const [recordingNotification, setRecordingNotification] = useState(false)
    const [recordingBlocked, setRecordingBlocked] = useState(false)

    const chunkWaitingIntervalId = useRef(0)

    const recording_session_label = useRef(undefined)
    const recorder = useRef()
    const voiceStream = useRef()
    const desktopStream = useRef()
    const stream = useRef()

    const videoBreakInterval = useRef()

    let newChunkAvailable = false;

    const {
        sendJsonMessage,
        lastJsonMessage,
    } = useWebSocket(wsBaseUrl+ `ws/game/${props.gameId}/stream`, {
        shouldReconnect: () => true,
        share: true
    });

    useEffect(() => {
        if (lastJsonMessage?.message === "recording_in_progress") {
            setRecordingNotification(true)
        } else if (lastJsonMessage?.message === "recording_stopped" && !recordingInProgress) {
            setRecordingNotification(false)
        }
    }, [lastJsonMessage]);

    function sendRecordingNotification(started) {
        sendJsonMessage({message: started ? "recording_in_progress" : "recording_stopped"})
    }

    function prestartVideo() {
        if(!('getDisplayMedia' in navigator.mediaDevices)) {
            window.alert("Your browser does not support screen recording")
        } else {
            setRecordingRequested(true)
        }
    }

    function getRecordingLabel() {

        function extendDateUnitWithZero(dateUnit) {
            if (dateUnit < 0) {
                return "00"
            } else if (dateUnit < 10) {
                return "0" + dateUnit
            } else {
                return "" + dateUnit
            }
        }

        const d = new Date();

        const year = extendDateUnitWithZero(d.getFullYear())
        const month = extendDateUnitWithZero(d.getMonth() + 1)
        const date = extendDateUnitWithZero(d.getDate())
        const hours = extendDateUnitWithZero(d.getHours())
        const minutes = extendDateUnitWithZero(d.getMinutes())
        const seconds = extendDateUnitWithZero(d.getSeconds())

        return year + "-" + month + "-" + date + "-" + hours + "-" + minutes + "-" + seconds
    }


    async function uploadToS3(chunk) {
        const time = chunk.timecode + ""
        const timestamp = time.split(".")[0]
        const label = recording_session_label.current
        newChunkAvailable = true

        const file = new File(
            [chunk.data],
            props.name.toLowerCase() + "_" + label + "_" + timestamp + ".mp4",
            {type: 'video/mp4'})

        const formData  = new FormData();

        for(const key in awsRequest.data.fields) {
            formData.append(key, awsRequest.data.fields[key]);
        }

        formData.append("file", file)

        fetchWithRetry(
            awsRequest.data.url,
            {
                method: "POST",
                body: formData
            },
            20000,
            30
        )
    }

    function onChunkAvailable(chunk) {
        newChunkAvailable = true
        sendRecordingNotification(true)
        uploadToS3(chunk)
    }

    const mergeAudioStreams = (desktopStream, voiceStream) => {
        const context = new AudioContext();
        const destination = context.createMediaStreamDestination();

        try {
            const source1 = context.createMediaStreamSource(desktopStream);
            const desktopGain = context.createGain();
            desktopGain.gain.value = 1;
            source1.connect(desktopGain).connect(destination);
        } catch (e) {
            stopRecording()
            window.alert("failed to capture desktop audio")
        }

        const source2 = context.createMediaStreamSource(voiceStream);
        const voiceGain = context.createGain();
        voiceGain.gain.value = 1;
        source2.connect(voiceGain).connect(destination);

        return  destination.stream.getAudioTracks()
    };

    async function startRecording() {
        setRecordingRequested(false)
        recording_session_label.current = getRecordingLabel()
        try {
            desktopStream.current = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: {
                    restrictOwnAudio: false,
                    echoCancellation: false
                },
                preferCurrentTab: true,
                systemAudio: "include"
            }
            );

            if (desktopStream.current.getVideoTracks().length < 1 || desktopStream.current.getAudioTracks().length < 1) {
                setRecordingBlocked(true)
                return
            }

            desktopStream.current.getVideoTracks()[0].onended = (event) => {
                stopRecording()
                window.alert("Recording stopped: lost video")
            }
            desktopStream.current.getAudioTracks()[0].onended = (event) => {
                stopRecording()
                window.alert("Recording stopped: lost audio")
            }

            voiceStream.current = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
            sendRecordingNotification(true)
        } catch (e) {
            window.alert(JSON.stringify(e.message))
            return
        }


        const tracks = [
            ...desktopStream.current.getVideoTracks(),
            ...mergeAudioStreams(desktopStream.current, voiceStream.current)
        ];


        stream.current = new MediaStream(tracks);
        // recorder.current = new MediaRecorder(stream.current, {mimeType: 'video/webm; codecs=h264'});
        // const r = new MediaRecorder(stream.current, {mimeType: 'video/mp4; codecs="avc1.424028, mp4a.40.2"'});
        // r.requestData()
        // r.ondataavailable()
        // recorder.current = new MediaRecorder(stream.current, {mimeType: 'video/mp4; codecs="avc1.424028, mp4a.40.2"'});
        recorder.current = new MediaRecorder(stream.current, {mimeType: 'video/webm; codecs=h264'});
        recorder.current.ondataavailable = (e) => onChunkAvailable(e);
        recorder.onerror = (e) => {
            window.alert("Recording failed " + JSON.stringify(e))
            setRecordingRequested(false)
            setRecordingPaused(false)
            setRecordingInProgress(false)
            setRecordingNotification(false)
            sendRecordingNotification(false)
        }

        recorder.current.onstop = async () => {
            sendRecordingNotification(false)
        };

        recorder.current.start(30000)
        setRecordingInProgress(true)
        setRecordingNotification(true)
        sendRecordingStartedUpdate(props.gameId, ()=>{}, ()=>{})
        videoBreakInterval.current = setInterval(breakVideo, 90*60*1000 + 55*100)
    }

    function stopRecording() {
        if (recorder.current) {
            recorder.current.stop()
        }
        setRecordingInProgress(false)
        setRecordingNotification(false)
        setRecordingPaused(false)
        desktopStream.current.getTracks().forEach(s=>s.stop())
        voiceStream.current.getTracks().forEach(s=>s.stop())
        clearInterval(videoBreakInterval.current)
    }

    function pauseRecording() {
        setRecordingPaused(true)
        recorder.current.pause()
        sendRecordingNotification(false)
    }

    function resumeRecording() {
        setRecordingPaused(false)
        recorder.current.resume()
        sendRecordingNotification(true)
    }

    async function breakVideo() {
        if (recordingPaused) {
            return
        }

        newChunkAvailable = false
        await recorder.current.stop()
        chunkWaitingIntervalId.current = setInterval( async ()=> {
            if (newChunkAvailable) {
                newChunkAvailable = false
                clearInterval(chunkWaitingIntervalId.current)
                recording_session_label.current = getRecordingLabel()

                await recorder.current.start(30000)
            }
        }, 200)

    }


    useEffect(() => {
        UserService.getPresignedS3Request(
            props.gameId,
            (json)=>{
                setAwsRequest(json)
            },
            (message)=>{window.alert("Recording failed: failed to get upload credentials" + JSON.stringify(message))}
        )
    }, [props.gameId])

    function renderTooltip(text) {
        return (<Tooltip id="button-tooltip">
            {text}
        </Tooltip>)
    }


    return(
        <div className={props.controlsHorizontal ? VideoRecorderStyle.recorderContainerHorizontal : VideoRecorderStyle.recorderContainer}>
            {recordingRequested && <VideoRecorderPrestart close={() => setRecordingRequested(false)} confirm={startRecording}/>}
            <Modal
                show={recordingBlocked}
                onHide={() => setRecordingBlocked(false)}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <ModalHeader closeButton>
                    <ModalTitle>{t('recordingBlockedByBrowser')}</ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <span>{t('browserDoesNotSupport')}</span>
                    <br/>
                    <span>{t('Suppoerted browsers')}</span>
                    <ul>
                        <li>
                            <img src={"https://www.google.com/chrome/static/images/chrome-logo.svg"} width={20} height={20} alt={"Chrome"}></img>
                            <span> Google chrome</span>
                        </li>
                        <li>
                            <img src={"https://upload.wikimedia.org/wikipedia/commons/9/98/Microsoft_Edge_logo_%282019%29.svg"} width={20} height={20} alt={"Microsoft Edge"}></img>
                            <span> MicroSoft Edge</span>
                        </li>

                        <li>
                            <img src={"https://upload.wikimedia.org/wikipedia/commons/2/20/Opera_Touch_icon.svg"} width={20} height={20} alt={"Opera"}></img>
                            <span> Opera*</span>
                        </li>
                    </ul>
                </ModalBody>
                <ModalFooter>
                    <Button variant="primary" active={true} onClick={() => setRecordingBlocked(false)}>OK</Button>
                </ModalFooter>
            </Modal>
            <div>
                <OverlayTrigger overlay={renderTooltip(t('recordOngoing'))}>
                    <div className={
                        recordingNotification
                            ? recordingPaused ? VideoRecorderStyle.recordingPaused : VideoRecorderStyle.recordingInProgress
                            : VideoRecorderStyle.recordingNotInProgress}>
                    </div>
                </OverlayTrigger>
            </div>

            {!recordingInProgress &&
                <OverlayTrigger
                    overlay={renderTooltip(t('startRecord'))}>
                    <RadioButtonCheckedOutlinedIcon sx={{ color: '#e10000', cursor: "pointer"}} fontSize={"large"} onClick={prestartVideo}/>
                </OverlayTrigger>
            }
            {recordingInProgress &&
                <OverlayTrigger
                    overlay={renderTooltip(t('stopRecord'))}>
                    <StopCircleOutlinedIcon sx={{ color: '#ffffff', cursor: "pointer" }} fontSize={"large"}  onClick={stopRecording}/>
                </OverlayTrigger>
            }

            {!recordingPaused &&
                <OverlayTrigger
                    overlay={renderTooltip(t('pauseRecord'))}>
                    <PauseCircleOutlinedIcon
                    sx={recordingInProgress ? { color: '#ffffff', cursor: "pointer"} : {color: 'rgba(255,255,255,0.47)', cursor: "pointer"}}
                    fontSize={"large"}
                    onClick={recordingInProgress ? pauseRecording : () => {}}/>
                </OverlayTrigger>
            }
            {recordingPaused &&
                <OverlayTrigger
                    overlay={renderTooltip(t('renewRecord'))}>
                    <NotStartedOutlinedIcon
                    sx={recordingInProgress ? { color: '#ffffff', cursor: "pointer"} : {color: 'rgba(255,255,255,0.47)' ,cursor: "pointer"}}
                    fontSize={"large"}
                    onClick={recordingInProgress ? resumeRecording : () => {}}/>
                </OverlayTrigger>
            }

        </div>
    )

}

export default VideoRecorder